
import React, { lazy, Suspense } from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import CityLandingPageSEO from '@/components/seo/CityLandingPageSEO';
import MassiveLocalizedCityHero from '@/components/city/MassiveLocalizedCityHero';
import CityTherapistSection from '@/components/city/CityTherapistSection';
import CityQuickFilters from '@/components/city/CityQuickFilters';
import { useCityFromDatabase } from '@/hooks/useCityFromDatabase';
import { CityLanguage } from '@/context/languages/languageData';
import { extractLanguageFromPath } from '@/utils/routing/multilingualRouting';
import { detectCityLanguage } from '@/utils/cityLanguageDetector';
import NotFound from './NotFound';
import CityNotFoundFallback from '@/components/city/CityNotFoundFallback';
import UniqueCityContent from '@/components/city/UniqueCityContent';

// Lazy load heavy components for better performance
const CitySpecialtySection = lazy(() => import('@/components/city/CitySpecialtySection'));
const SpecialtiesGrid = lazy(() => import('@/components/city/SpecialtiesGrid'));
const CityTherapeuticServices = lazy(() => import('@/components/city/CityTherapeuticServices'));
const CityContentExpansion = lazy(() => import('@/components/city/CityContentExpansion'));
const NearbyCitiesSection = lazy(() => import('@/components/city/NearbyCitiesSection'));
const CityMentalHealthResources = lazy(() => import('@/components/city/CityMentalHealthResources'));
const CityTherapyApproaches = lazy(() => import('@/components/city/CityTherapyApproaches'));
const CityPsychologyInsights = lazy(() => import('@/components/city/CityPsychologyInsights'));
const CityInfoSection = lazy(() => import('@/components/city/CityInfoSection'));
const CityPsychologyFAQ = lazy(() => import('@/components/city/CityPsychologyFAQ'));
const FinalConversionCTA = lazy(() => import('@/components/city/FinalConversionCTA'));
const SEOContentHub = lazy(() => import('@/components/city/seo/SEOContentHub'));
const PerformanceOptimizer = lazy(() => import('@/components/city/seo/PerformanceOptimizer'));

// FASE 4: New content sections
const WhyChooseTherapySection = lazy(() => import('@/components/city/WhyChooseTherapySection'));
const MentalHealthStatsSection = lazy(() => import('@/components/city/MentalHealthStatsSection'));
const InsurancePaymentSection = lazy(() => import('@/components/city/InsurancePaymentSection'));
const TherapeuticApproachesSection = lazy(() => import('@/components/city/TherapeuticApproachesSection'));
const SuccessStoriesSection = lazy(() => import('@/components/city/SuccessStoriesSection'));
const InternalLinksSection = lazy(() => import('@/components/city/InternalLinksSection'));

// Loading fallback component
const SectionLoader = () => (
  <div className="py-12 flex justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

const LocalizedCityLanding = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const location = useLocation();
  
  if (!citySlug) {
    return <NotFound />;
  }
  
  // Extract language from pathname
  const urlLanguage = extractLanguageFromPath(location.pathname) || 'en';
  
  // Load city from database
  const { data: city, isLoading, error } = useCityFromDatabase(citySlug);
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  // If city not found, show intelligent fallback instead of generic 404
  if (!city || error) {
    return <CityNotFoundFallback citySlug={citySlug} />;
  }

  // Validate language for this city
  const localLang = detectCityLanguage(city);
  const validLanguages = localLang === 'en' ? ['en'] : ['en', localLang];
  const language = validLanguages.includes(urlLanguage as CityLanguage) 
    ? (urlLanguage as CityLanguage) 
    : 'en';
  
  // Redirect if invalid language
  if (urlLanguage !== 'en' && !validLanguages.includes(urlLanguage as CityLanguage)) {
    return <Navigate to={`/city/${citySlug}`} replace />;
  }

  return (
    <>
      <CityLandingPageSEO city={city} language={language} />
      <PerformanceOptimizer />
      
      {/* Hero Section - Above the fold */}
      <MassiveLocalizedCityHero city={city} language={language} />
      
      {/* Quick Filters - Above the fold for better UX */}
      <CityQuickFilters city={city} language={language} />
      
      {/* Therapist Section - Priority content */}
      <CityTherapistSection city={city} language={language} />
      
      {/* FASE 4: Unique SEO Content - 3-4 paragraphs per city */}
      <Suspense fallback={<SectionLoader />}>
        <UniqueCityContent city={city} language={language} />
      </Suspense>
      
      {/* FASE 4: New Enhanced Content Sections */}
      <Suspense fallback={<SectionLoader />}>
        <WhyChooseTherapySection city={city} language={language} />
      </Suspense>
      
      <Suspense fallback={<SectionLoader />}>
        <MentalHealthStatsSection city={city} language={language} />
      </Suspense>
      
      {/* Specialties Grid - Core functionality */}
      <Suspense fallback={<SectionLoader />}>
        <SpecialtiesGrid city={city} language={language} />
      </Suspense>
      
      {/* Specialty Section */}
      <Suspense fallback={<SectionLoader />}>
        <CitySpecialtySection city={city} language={language} />
      </Suspense>
      
      {/* FASE 4: Therapeutic Approaches */}
      <Suspense fallback={<SectionLoader />}>
        <TherapeuticApproachesSection city={city} language={language} />
      </Suspense>
      
      {/* Therapeutic Services */}
      <Suspense fallback={<SectionLoader />}>
        <CityTherapeuticServices city={city} language={language} />
      </Suspense>
      
      {/* FASE 4: Insurance & Payment */}
      <Suspense fallback={<SectionLoader />}>
        <InsurancePaymentSection city={city} language={language} />
      </Suspense>
      
      {/* Content Expansion */}
      <Suspense fallback={<SectionLoader />}>
        <CityContentExpansion city={city} language={language} />
      </Suspense>
      
      {/* Therapy Approaches */}
      <Suspense fallback={<SectionLoader />}>
        <CityTherapyApproaches city={city} language={language} />
      </Suspense>
      
      {/* Psychology Insights */}
      <Suspense fallback={<SectionLoader />}>
        <CityPsychologyInsights city={city} language={language} />
      </Suspense>
      
      {/* Mental Health Resources */}
      <Suspense fallback={<SectionLoader />}>
        <CityMentalHealthResources city={city} language={language} />
      </Suspense>
      
      {/* FASE 4: Success Stories */}
      <Suspense fallback={<SectionLoader />}>
        <SuccessStoriesSection city={city} language={language} />
      </Suspense>
      
      {/* City Info Section */}
      <Suspense fallback={<SectionLoader />}>
        <CityInfoSection city={city} language={language} />
      </Suspense>
      
      {/* Nearby Cities */}
      <Suspense fallback={<SectionLoader />}>
        <NearbyCitiesSection city={city} language={language} />
      </Suspense>
      
      {/* FASE 4: Internal Links Hub */}
      <Suspense fallback={<SectionLoader />}>
        <InternalLinksSection city={city} language={language} />
      </Suspense>
      
      {/* FAQ Section */}
      <Suspense fallback={<SectionLoader />}>
        <CityPsychologyFAQ city={city} language={language} />
      </Suspense>
      
      {/* SEO Content Hub - 20 categories with unique content */}
      <Suspense fallback={<SectionLoader />}>
        <SEOContentHub city={city} language={language} />
      </Suspense>
      
      {/* Final CTA */}
      <Suspense fallback={<SectionLoader />}>
        <FinalConversionCTA city={city} language={language} />
      </Suspense>
    </>
  );
};

export default LocalizedCityLanding;
